import type { ReactNode } from 'react'
import { ContentGroup } from '@/shared/ui'
import { designTokens } from '@/shared/lib/design-tokens'
import { cn } from '@/shared/lib'

export type AuthPanelVariant = 'desktop' | 'mobile' | 'mobileB2B'

export type AuthPanelProps = {
  title: string
  subtitle?: string
  backLink?: ReactNode
  footer?: ReactNode
  variant?: AuthPanelVariant
  minHeight?: string | null
  className?: string
  onSubmit?: () => void
  children: ReactNode
}

const variantMap: Record<
  AuthPanelVariant,
  'registrationDesktop' | 'registrationMobile' | 'registrationMobileB2B'
> = {
  desktop: 'registrationDesktop',
  mobile: 'registrationMobile',
  mobileB2B: 'registrationMobileB2B',
}

const typographyStyle = (token: { size: string; lineHeight: string; weight: number }) => ({
  fontFamily: designTokens.typography.fontFamilies.primary,
  fontSize: token.size,
  lineHeight: token.lineHeight,
  fontWeight: token.weight,
})

export function AuthPanel({
  title,
  subtitle,
  backLink,
  footer,
  variant = 'desktop',
  minHeight,
  className,
  onSubmit,
  children,
}: AuthPanelProps) {
  const resolvedMinHeight =
    minHeight === null
      ? undefined
      : (minHeight ??
        (variant === 'mobile' ? designTokens.sizes.minHeight.authPanelMobile : undefined))
  const isMobile = variant === 'mobile' || variant === 'mobileB2B'

  const headerBlock = (
    <>
      {backLink ? (
        <div className="flex w-full items-center" style={{ gap: designTokens.spacing.xs }}>
          {backLink}
        </div>
      ) : null}
      <div
        className="flex w-full flex-col items-center"
        style={{ gap: subtitle ? designTokens.spacing.xs : designTokens.spacing.none }}
      >
        <h2
          style={{
            ...typographyStyle(designTokens.typography.heading.h2),
            color: designTokens.colors.text.primary,
            textAlign: 'center',
            width: '100%',
          }}
        >
          {title}
        </h2>
        {subtitle ? (
          <p
            style={{
              ...typographyStyle(designTokens.typography.body.base),
              color: designTokens.colors.text.primary,
              textAlign: 'center',
            }}
          >
            {subtitle}
          </p>
        ) : null}
      </div>
    </>
  )

  const topGroup = (
    <div
      className="flex w-full flex-col"
      style={{ gap: isMobile ? designTokens.spacing.xxl : designTokens.spacing.popupModalGap }}
    >
      {headerBlock}
      <div className="w-full">{children}</div>
    </div>
  )

  const panelContent = (
    <ContentGroup
      variant={variantMap[variant]}
      className={cn('w-full', resolvedMinHeight ? 'h-full' : undefined, className)}
      style={resolvedMinHeight ? { minHeight: resolvedMinHeight } : undefined}
    >
      {topGroup}
      {footer ? <div className="w-full">{footer}</div> : null}
    </ContentGroup>
  )

  const panel = onSubmit ? (
    <form
      className="w-full"
      onSubmit={(event) => {
        event.preventDefault()
        onSubmit()
      }}
      noValidate
    >
      {panelContent}
    </form>
  ) : (
    panelContent
  )

  if (!resolvedMinHeight) {
    return panel
  }

  return <div className="w-full">{panel}</div>
}
